import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { Button } from "@/dentlab/src/components/ui/button";
import { useFileUrlCacheStore } from "@/lib/store/file-url-cache.store";
import { getFileType, type FileType } from "@/lib/utils/file-utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ThumbnailStrip } from "./components/thumbnail-strip.component";
import { FileViewerModalHeader } from "./components/file-viewer-modal-header.component";
import { FileContent } from "../file-content.component";
import { useStorage } from "@/dentlab/src/hooks/useStorage";
import { Logger } from "@/lib/logger/Logger";
import { addRecentFile } from "@/lib/utils/recents";

interface FileViewerModalProps {
	/** Single file ID or array of file IDs to display */
	fileIds: string | string[];
	/** Whether the modal is open */
	isOpen: boolean;
	/** Callback when the modal should close */
	onClose: () => void;
	/** Initial file ID to display (when multiple files) */
	defaultFileId?: string;
	metadata?: { jobId?: number | null; clientId?: string | null };
	/** Direct blob URL — bypasses file ID resolution */
	blobUrl?: string;
	/** File name to display when using blobUrl */
	blobFileName?: string;
}

/** Neighbours on each side whose signed URLs (and thumbnails) are prepared ahead of navigation. */
const PRELOAD_RADIUS = 5;

interface FileInfo {
	id: string;
	name: string;
	type: FileType;
	url: string;
}

function getNeighborFileIds(
	fileIds: string[],
	centerIndex: number,
	radius: number
): string[] {
	const ids: string[] = [];
	for (let i = 1; i <= radius; i++) {
		if (centerIndex - i >= 0) ids.push(fileIds[centerIndex - i]);
		if (centerIndex + i < fileIds.length) ids.push(fileIds[centerIndex + i]);
	}
	return ids;
}

/**
 * Full-screen file viewer modal with gallery support
 * Supports PDF, STL, images, and other file types
 */
export const FileViewerModal: React.FC<FileViewerModalProps> = ({
	fileIds,
	isOpen,
	onClose,
	defaultFileId,
	metadata,
	blobUrl,
	blobFileName,
}) => {
	// Normalize fileIds to array in case user passes a single file ID
	const fileIdsArray = useMemo(
		() => (Array.isArray(fileIds) ? fileIds : [fileIds]),
		[fileIds]
	);

	const getDefaultIndex = () =>
		defaultFileId ? Math.max(fileIdsArray.indexOf(defaultFileId), 0) : 0;

	const [currentIndex, setCurrentIndex] = useState(getDefaultIndex);
	// Jump to defaultFileId when the caller changes it or the list content while mounted. Adjusted
	// during render so the load effect never sees an index that is about to be replaced. Compared
	// by content: callers pass inline arrays that are new on every render.
	const syncKey = `${defaultFileId ?? ""}|${fileIdsArray.join(",")}`;
	const [syncedKey, setSyncedKey] = useState(syncKey);
	if (syncedKey !== syncKey) {
		setSyncedKey(syncKey);
		setCurrentIndex(getDefaultIndex());
	}
	const [isLoading, setIsLoading] = useState(true);
	const lastTrackedFileId = useRef<string | null>(null);

	const fileCache = useFileUrlCacheStore((state) => state.fileCache);
	const fileContent = useFileUrlCacheStore((state) => state.fileContent);
	const getFileIfCached = useFileUrlCacheStore((state) => state.getFileIfCached);
	const { downloadBlob } = useStorage();

	const currentFileId = fileIdsArray[currentIndex];

	const currentFile: FileInfo | null = useMemo(() => {
		const cached = getFileIfCached(currentFileId);
		if (!cached) return null;
		return {
			id: currentFileId,
			name: cached.file_name || "Unbenannte Datei",
			type: getFileType(
				cached.meta_data as Record<string, any> | null,
				cached.file_name
			),
			url: fileContent[currentFileId]?.url ?? cached.signed_url,
		};
	}, [fileCache, fileContent, currentFileId, getFileIfCached]);

	// Thumbnails render from downloaded content only, so a file is never fetched twice.
	const getThumbnailFile = useCallback(
		(fileId: string) => {
			const cached = getFileIfCached(fileId);
			if (!cached) return null;
			const content = fileContent[fileId];
			if (content) return { ...cached, signed_url: content.url };
			return cached.file_type === "video" ? cached : null;
		},
		[fileCache, fileContent, getFileIfCached]
	);

	if (isOpen && currentFile && currentFile.id !== lastTrackedFileId.current) {
		lastTrackedFileId.current = currentFile.id;
		addRecentFile({
			fileId: currentFile.id,
			fileName: currentFile.name,
			fileType: currentFile.type,
			jobId: metadata?.jobId ?? null,
			clientId: metadata?.clientId ?? null,
			timestamp: Date.now(),
		});
	}

	// Load the current file's content first, then its neighbours' in one batch. Content stays in the
	// cache, so reopening a file does not download it again. Bounded, so a long list (the archive
	// hands over every loaded file) does not keep downloading.
	useEffect(() => {
		if (!isOpen || fileIdsArray.length === 0) return;

		let cancelled = false;
		const store = useFileUrlCacheStore.getState();

		const loadInsideOut = async () => {
			const idx = currentIndex;
			const centerId = fileIdsArray[idx];

			if (!store.fileContent[centerId]) {
				setIsLoading(true);
				await store.loadFileContent(centerId);
				if (cancelled) return;
			}
			setIsLoading(false);

			const neighborIds = getNeighborFileIds(fileIdsArray, idx, PRELOAD_RADIUS);
			if (neighborIds.length === 0) return;
			try {
				await store.preloadFiles(neighborIds);
				await Promise.all(neighborIds.map(store.loadFileContent));
			} catch (error) {
				Logger.error("Error preloading neighbor files:" + error);
			}
		};

		loadInsideOut();

		return () => {
			cancelled = true;
		};
	}, [isOpen, currentIndex, fileIdsArray]);

	// Navigation handlers
	const handlePrevious = useCallback(() => {
		setCurrentIndex((prev) =>
			prev > 0 ? prev - 1 : fileIdsArray.length - 1
		);
	}, [fileIdsArray.length]);

	const handleNext = useCallback(() => {
		setCurrentIndex((prev) =>
			prev < fileIdsArray.length - 1 ? prev + 1 : 0
		);
	}, [fileIdsArray.length]);

	// Download handler
	const handleDownload = useCallback(async () => {
		if (!currentFile) return;

		try {
			const response = await fetch(currentFile.url);
			if (!response.ok) throw new Error("Download failed");

			const blob = await response.blob();
			await downloadBlob(blob, currentFile.name);
		} catch (error) {
			Logger.error("Download error:"+ error);
		}
	}, [currentFile, downloadBlob]);

	// Keyboard navigation and scroll lock
	useEffect(() => {
		if (!isOpen) return;

		// Lock body scroll
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		// Keyboard handlers
		const handleKeyDown = (e: KeyboardEvent) => {
			switch (e.key) {
				case "ArrowLeft":
					if (fileIdsArray.length > 1) handlePrevious();
					break;
				case "ArrowRight":
					if (fileIdsArray.length > 1) handleNext();
					break;
				case "Escape":
					onClose();
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		// Cleanup
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = originalOverflow;
		};
	}, [isOpen, fileIdsArray.length, handlePrevious, handleNext, onClose]);

	const renderFileContent = () => {
		if (blobUrl) {
			return (
				<FileContent
					url={blobUrl}
					fileType="pdf"
					fileName={blobFileName ?? "Dokument"}
				/>
			);
		}

		if (isLoading) {
			return (
				<FileContent
					url=""
					fileType={null}
					isLoading={true}
				/>
			);
		}

		if (!currentFile) {
			return <ErrorState />;
		}

		return (
			<FileContent
				url={currentFile.url}
				fileType={currentFile.type}
				fileName={currentFile.name}
				onDownload={handleDownload}
			/>
		);
	};

	// Don't render if not open
	if (!isOpen) return null;

	// Render modal with portal
	return ReactDOM.createPortal(
		<div className="fixed inset-0" style={{ zIndex: 60 }}>
			<div
				className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm"
				onClick={(e) => {
					e.stopPropagation();
					onClose();
				}}
				aria-label="Close modal"
				style={{ pointerEvents: "auto" }}
			/>

			<div className="relative h-full w-full flex flex-col" style={{ pointerEvents: "auto" }}>
				<FileViewerModalHeader
					fileName={blobUrl ? (blobFileName ?? "Dokument") : (currentFile?.name || "Datei")}
					onClose={onClose}
				/>

				<div className="h-full relative">
					{renderFileContent()}

					{/* Navigation arrows (only for multiple files) */}
					{fileIdsArray.length > 1 && !isLoading && (
						<NavigationArrows
							onPrevious={handlePrevious}
							onNext={handleNext}
							currentIndex={currentIndex}
							totalFiles={fileIdsArray.length}
						/>
					)}
				</div>

				{/* Thumbnail strip (only for multiple files) */}
				{fileIdsArray.length > 1 && !isLoading && (
					<ThumbnailStrip
						fileIds={fileIdsArray}
						currentIndex={currentIndex}
						onSelectFile={setCurrentIndex}
						getCachedFile={getThumbnailFile}
					/>
				)}
			</div>
		</div>,
		document.body
	);
};

const NavigationArrows: React.FC<{
	onPrevious: () => void;
	onNext: () => void;
	currentIndex: number;
	totalFiles: number;
}> = ({ onPrevious, onNext, currentIndex, totalFiles }) => (
	<>
		<Button
			variant="ghost"
			size="icon"
			className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm z-20"
			onClick={(e) => {
				e.stopPropagation();
				onPrevious();
			}}
			aria-label="Vorherige Datei"
			style={{ pointerEvents: "auto" }}
		>
			<ChevronLeft className="h-6 w-6" />
		</Button>
		<Button
			variant="ghost"
			size="icon"
			className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm z-20"
			onClick={(e) => {
				e.stopPropagation();
				onNext();
			}}
			aria-label="Nächste Datei"
			style={{ pointerEvents: "auto" }}
		>
			<ChevronRight className="h-6 w-6" />
		</Button>
		{/* File counter */}
		<div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
			{currentIndex + 1} / {totalFiles}
		</div>
	</>
);

const ErrorState: React.FC = () => (
	<div className="flex h-full items-center justify-center">
		<div className="text-center bg-red-500/10 backdrop-blur-sm rounded-lg p-8 max-w-md">
			<div className="text-red-400 mb-4">
				<svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
				</svg>
			</div>
			<h3 className="text-lg font-semibold text-white mb-2">Datei konnte nicht geladen werden</h3>
			<p className="text-white/70 text-sm">Die angeforderte Datei konnte nicht gefunden oder geladen werden.</p>
		</div>
	</div>
);

